import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Sparkles, ChevronDown, ChevronUp, BookOpen, Target, Heart, 
  Globe, Banknote, Loader2, RefreshCw, CheckCircle2, ListTodo
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryConfig = {
  passion: { icon: Heart, color: '#FF6B6B', label: { fr: 'Passion', en: 'Passion' } },
  talent: { icon: Target, color: '#FFE66D', label: { fr: 'Talent', en: 'Talent' } },
  cause: { icon: Globe, color: '#4ECDC4', label: { fr: 'Cause', en: 'Cause' } },
  career: { icon: Banknote, color: '#1A535C', label: { fr: 'Carrière', en: 'Career' } },
};

const DailyCoaching = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [tip, setTip] = useState(null);
  const [taskInfo, setTaskInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const fetchTip = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/coaching/daily`);
      const data = response.data;
      
      if (data.tip && typeof data.tip === 'object') {
        setTip(data.tip);
      } else if (data.tip && typeof data.tip === 'string') {
        setTip({ tip_title: '', tip_content: data.tip, action: '', category: 'passion' });
      }
      
      if (data.task_key) {
        setTaskInfo({
          task_key: data.task_key,
          task_description: data.task_description,
          week_theme: data.week_theme,
          tip_index: data.tip_index
        });
      }
    } catch (e) { console.warn('Coaching fetch:', e.message); } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchTip();
  }, [user, fetchTip]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/coaching/history?limit=10`);
      setHistory(response.data || []);
      setShowHistory(true);
    } catch (e) {
      console.warn('Coaching history:', e.message);
    }
  };

  if (!user || !user.purchases?.includes('diagnostic')) return null;

  if (loading) {
    return (
      <div className="card-ikigai p-6" data-testid="coaching-loading">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-[#4ECDC4]" />
          <span className="text-stone-500 text-sm">{fr ? 'Chargement du coaching...' : 'Loading coaching...'}</span>
        </div>
      </div>
    );
  }

  if (!tip) return null;

  const category = tip.category || 'passion';
  const config = categoryConfig[category] || categoryConfig.passion;
  const Icon = config.icon;

  return (
    <div className="card-ikigai overflow-hidden" data-testid="daily-coaching">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-5 cursor-pointer hover:bg-stone-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${config.color}15` }}>
            <Sparkles className="w-5 h-5" style={{ color: config.color }} />
          </div>
          <div>
            <h3 className="font-serif text-base font-semibold text-stone-900">
              {fr ? 'Coaching du jour' : "Today's coaching"}
            </h3>
            {taskInfo && (
              <div className="flex items-center gap-1.5 mt-0.5">
                <ListTodo className="w-3 h-3 text-[#4ECDC4]" />
                <span className="text-xs text-stone-400">
                  {fr ? `Tâche : ${taskInfo.task_description}` : `Task: ${taskInfo.task_description}`}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: `${config.color}15`, color: config.color }}>
            {config.label[language] || config.label.fr}
          </span>
          {expanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
        </div>
      </div>

      {expanded && (
        <div className="px-5 pb-5 space-y-4">
          {/* Task context banner */}
          {taskInfo && (
            <div className="bg-[#4ECDC4]/5 border border-[#4ECDC4]/20 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4 text-[#4ECDC4]" />
                <span className="text-xs font-semibold text-[#4ECDC4] uppercase">
                  {taskInfo.week_theme || (fr ? 'Tâche en cours' : 'Current task')}
                </span>
              </div>
              <p className="text-sm text-stone-700 font-medium">{taskInfo.task_description}</p>
              <div className="flex items-center gap-1 mt-1">
                {[0, 1, 2].map(i => (
                  <div
                    key={i}
                    className="h-1.5 flex-1 rounded-full"
                    style={{
                      backgroundColor: i <= (taskInfo.tip_index || 0) ? config.color : '#E5E7EB'
                    }}
                  />
                ))}
                <span className="text-xs text-stone-400 ml-2">
                  {fr ? `Jour ${(taskInfo.tip_index || 0) + 1}/3` : `Day ${(taskInfo.tip_index || 0) + 1}/3`}
                </span>
              </div>
            </div>
          )}

          {/* Tip content */}
          {tip.tip_title && (
            <h4 className="font-serif text-lg font-semibold text-stone-900">{tip.tip_title}</h4>
          )}
          <p className="text-stone-600 text-sm leading-relaxed">{tip.tip_content}</p>
          
          {tip.action && (
            <div className="bg-stone-50 rounded-xl p-4 border-l-4" style={{ borderColor: config.color }}>
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-4 h-4" style={{ color: config.color }} />
                <span className="text-xs font-semibold text-stone-500 uppercase">
                  {fr ? "Action du jour" : "Today's action"}
                </span>
              </div>
              <p className="text-stone-800 text-sm font-medium">{tip.action}</p>
            </div>
          )}

          {/* History button */}
          <div className="flex justify-end pt-2">
            <button
              onClick={fetchHistory}
              className="text-xs text-stone-400 hover:text-stone-600 flex items-center gap-1 transition-colors"
              data-testid="coaching-history-btn"
            >
              <BookOpen className="w-3.5 h-3.5" />
              {fr ? 'Voir l\'historique' : 'View history'}
            </button>
          </div>

          {/* History */}
          {showHistory && history.length > 0 && (
            <div className="border-t border-stone-100 pt-4 mt-2 space-y-3">
              <h4 className="text-sm font-semibold text-stone-700">
                {fr ? 'Derniers conseils' : 'Recent tips'}
              </h4>
              {history.map((item, i) => {
                const itemTip = item.tip || {};
                const itemCategory = (typeof itemTip === 'object' ? itemTip.category : '') || 'passion';
                const itemConfig = categoryConfig[itemCategory] || categoryConfig.passion;
                return (
                  <div key={i} className="flex gap-3 p-3 bg-stone-50 rounded-lg">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${itemConfig.color}15` }}>
                      <itemConfig.icon className="w-4 h-4" style={{ color: itemConfig.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs text-stone-400">{item.date}</span>
                        {item.task_description && (
                          <span className="text-xs text-[#4ECDC4] truncate">
                            {item.task_description}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-stone-600">
                        {typeof itemTip === 'object' ? itemTip.tip_title || itemTip.tip_content : itemTip}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DailyCoaching;
